// @ts-nocheck
// ═══ Gathering Studio — Parameterized Remotion Composition ═══
// Every visual property comes from inputProps. No hardcoded values.
// This is the component that the Remotion Player renders.
//
// Audio reactivity: wrap content in <AudioReactivityProvider>, then any
// child component calls useAudioReactivity() to get per-frame data.

import {
  AbsoluteFill,
  Audio,
  Sequence,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  staticFile,
} from 'remotion'
import { AudioReactivityProvider, useAudioReactivity } from './AudioReactivity'
import { BeatProvider, useBeatContext } from './BeatDetection'
import { PostProcessingStack } from './PostProcessing'
import { AudioParticles } from './Particles'
import type { StudioProject, LyricBlock, Section, VisualSettings } from './types'

export interface CompositionProps {
  project: StudioProject
}

export const StudioComposition: React.FC<CompositionProps> = ({ project }) => {
  const { audio } = project
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const currentMs = (frame / fps) * 1000

  return (
    <AudioReactivityProvider audioUrl={audio?.url}>
      <BeatProvider audioUrl={audio?.url} currentMs={currentMs}>
        <StudioCanvas project={project} />
      </BeatProvider>
    </AudioReactivityProvider>
  )
}

// ═══ Canvas (inside audio provider) ═══

const StudioCanvas: React.FC<{ project: StudioProject }> = ({ project }) => {
  const frame = useCurrentFrame()
  const { fps, width } = useVideoConfig()
  const currentMs = (frame / fps) * 1000

  const { lyrics, sections, visuals, audio } = project
  const currentSection = sections.find(s => currentMs >= s.startMs && currentMs < s.endMs)
  const bgColor = currentSection?.backgroundColor || visuals.defaultBackgroundColor

  const { bass, mid, high, volume, spectrum } = useAudioReactivity()
  const { isOnBeat, beatIntensity, bpm } = useBeatContext()

  // ── Demo effect 1: Background pulse on bass ──
  const bgPulse = interpolate(bass, [0, 0.8], [1, 1.15], { extrapolateRight: 'clamp' })
  const bgVignette = interpolate(volume, [0, 0.5], [0.3, 0.1], { extrapolateRight: 'clamp' })

  // ── Demo effect 2: Color shift on frequency ──
  // High frequencies shift the background toward the section accent color
  const colorShiftOpacity = interpolate(high, [0, 0.4], [0, 0.25], { extrapolateRight: 'clamp' })
  const accentColor = currentSection?.accentColor || visuals.defaultFontColor

  // ── Demo effect 3: Opacity breathing on volume (ambient glow layer) ──
  const breathOpacity = interpolate(volume, [0, 0.3, 0.6], [0.02, 0.06, 0.12], { extrapolateRight: 'clamp' })

  // ── Background images with crossfade ──
  const prevSection = sections.find(s => currentMs >= s.endMs && currentMs < s.endMs + 500)
  const currentBgImage = currentSection?.backgroundImage
  const prevBgImage = prevSection?.backgroundImage
  const crossfadeProgress = prevSection
    ? interpolate(currentMs, [prevSection.endMs, prevSection.endMs + 500], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
    : 1

  return (
    <AbsoluteFill style={{ backgroundColor: bgColor, transition: 'background-color 0.5s ease' }}>
      {/* Background images — crossfade between sections */}
      {prevBgImage && crossfadeProgress < 1 && (
        <AbsoluteFill style={{
          backgroundImage: `url(${prevBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 1 - crossfadeProgress,
          pointerEvents: 'none',
        }} />
      )}
      {currentBgImage && (
        <AbsoluteFill style={{
          backgroundImage: `url(${currentBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: crossfadeProgress,
          pointerEvents: 'none',
        }} />
      )}

      {/* Audio track */}
      {audio?.url && (
        <Audio src={audio.url} />
      )}

      {/* Audio-reactive background glow (bass pulse) */}
      <AbsoluteFill style={{
        background: `radial-gradient(ellipse at center, rgba(255,255,255,${bgVignette * bgPulse * 0.08}) 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Audio-reactive color shift (high frequencies → accent wash) */}
      {colorShiftOpacity > 0.01 && (
        <AbsoluteFill style={{
          backgroundColor: accentColor,
          opacity: colorShiftOpacity,
          mixBlendMode: 'soft-light',
          pointerEvents: 'none',
        }} />
      )}

      {/* Audio-reactive breathing glow (volume → ambient radial pulse) */}
      <AbsoluteFill style={{
        background: `radial-gradient(circle at 50% 50%, ${accentColor}${Math.round(breathOpacity * 255).toString(16).padStart(2, '0')} 0%, transparent 60%)`,
        pointerEvents: 'none',
      }} />

      {/* Beat flash — brief white flash on detected beats */}
      {beatIntensity > 0.1 && (
        <AbsoluteFill style={{
          backgroundColor: '#ffffff',
          opacity: beatIntensity * 0.06,
          pointerEvents: 'none',
        }} />
      )}

      {/* Audio-reactive spectrum bars (subtle, bottom edge) */}
      {spectrum.length > 0 && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 60, display: 'flex', alignItems: 'flex-end',
          justifyContent: 'center', gap: 2, opacity: 0.15,
          pointerEvents: 'none',
        }}>
          {spectrum.slice(0, 32).map((val, i) => (
            <div key={i} style={{
              width: Math.max(2, (width / 32) - 4),
              height: val * 60,
              backgroundColor: accentColor,
              borderRadius: '2px 2px 0 0',
              transition: 'height 0.05s ease',
            }} />
          ))}
        </div>
      )}

      {/* Audio-reactive particles */}
      {project.effects?.filter(e => e.type === 'particles' && e.enabled).map(fx => (
        <AudioParticles
          key={fx.id}
          config={{
            count: (fx.settings?.count as number) || 60,
            color: (fx.settings?.color as string) || accentColor,
            speed: (fx.settings?.speed as number) || 0.3,
            audioReactive: true,
          }}
          opacity={fx.opacity}
        />
      ))}

      {/* Lyric blocks */}
      {lyrics.map(block => {
        const startFrame = Math.round((block.startMs / 1000) * fps)
        const endFrame = Math.round((block.endMs / 1000) * fps)
        const durationFrames = endFrame - startFrame

        if (durationFrames <= 0) return null

        return (
          <Sequence key={block.id} from={startFrame} durationInFrames={durationFrames}>
            <LyricRenderer
              block={block}
              visuals={visuals}
              section={currentSection}
            />
          </Sequence>
        )
      })}

      {/* Post-processing effects (rendered after lyrics — overlays everything) */}
      <PostProcessingStack
        effects={project.effects}
        sectionIntensity={currentSection?.intensity ?? 0.5}
      />
    </AbsoluteFill>
  )
}

// ═══ Lyric Renderer ═══
// Uses useAudioReactivity() directly — no prop drilling needed.

const LyricRenderer: React.FC<{
  block: LyricBlock
  visuals: VisualSettings
  section?: Section
}> = ({ block, visuals, section }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { bass, mid, volume } = useAudioReactivity()

  const style = block.style || {}
  const fontSize = style.fontSize || visuals.defaultFontSize
  const fontFamily = style.fontFamily || visuals.defaultFontFamily
  const color = style.color || visuals.defaultFontColor
  const animation = style.animation || visuals.defaultAnimation

  const totalFrames = Math.round(((block.endMs - block.startMs) / 1000) * fps)
  const fadeIn = Math.min(10, totalFrames * 0.2)
  const fadeOut = Math.min(8, totalFrames * 0.2)

  // Opacity envelope
  const opacity = interpolate(
    frame,
    [0, fadeIn, Math.max(fadeIn + 1, totalFrames - fadeOut), totalFrames],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )

  // Drift animation
  const translateY = animation === 'drift'
    ? interpolate(frame, [0, fadeIn, totalFrames - fadeOut, totalFrames], [20, 0, 0, -10], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
    : 0

  // Scale animation
  const scale = animation === 'scale'
    ? interpolate(frame, [0, fadeIn], [0.8, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
    : 1

  // Power word glow + audio-reactive glow
  const isPower = block.isPowerWord
  const baseGlow = isPower
    ? interpolate(Math.sin(frame * 0.08), [-1, 1], [0.3, 0.7])
    : 0
  const audioGlow = interpolate(bass, [0, 0.6], [0, 0.4], { extrapolateRight: 'clamp' })
  const glowIntensity = Math.min(1, baseGlow + audioGlow)

  // Audio-reactive text scale — text subtly breathes with volume (beat pulse)
  const audioScale = 1 + interpolate(volume, [0, 0.5], [0, 0.03], { extrapolateRight: 'clamp' })

  if (block.mode === 'word') {
    return (
      <AbsoluteFill style={{
        justifyContent: 'center', alignItems: 'center',
        opacity,
        transform: `translateY(${translateY}px) scale(${scale * audioScale})`,
      }}>
        <span style={{
          fontFamily,
          fontSize: isPower ? fontSize * 1.3 : fontSize,
          fontWeight: isPower ? 700 : 400,
          color,
          textAlign: 'center',
          maxWidth: '85%',
          lineHeight: 1.3,
          textShadow: isPower
            ? `0 0 ${14 + glowIntensity * 18}px ${color}80, 0 2px 4px rgba(0,0,0,0.3)`
            : '0 2px 8px rgba(0,0,0,0.5)',
        }}>
          {block.text}
        </span>
      </AbsoluteFill>
    )
  }

  // Line mode — kinetic typography with animation variants
  const words = block.text.split(/\s+/)

  const renderWord = (word: string, i: number) => {
    const staggerDelay = animation === 'typewriter' ? i * 2 : i * 3
    const localFrame = Math.max(0, frame - staggerDelay)

    const springConfig = animation === 'elastic'
      ? { damping: 8, stiffness: 180, mass: 0.4 }
      : animation === 'stagger'
      ? { damping: 12, stiffness: 120, mass: 0.6 }
      : animation === 'split-scale'
      ? { damping: 10, stiffness: 200, mass: 0.3 }
      : animation === 'bounce'
      ? { damping: 6, stiffness: 280, mass: 0.5 }
      : animation === 'wave'
      ? { damping: 12, stiffness: 80, mass: 0.8 }
      : animation === 'glitch'
      ? { damping: 15, stiffness: 200, mass: 0.3 }
      : { damping: 10, stiffness: 100, mass: 0.5 }

    const enter = spring({ frame: localFrame, fps, config: springConfig })

    let wordOpacity = interpolate(enter, [0, 0.4, 1], [0, 0.6, 1])
    let wordY = 0
    let wordScale = 1
    let wordRotate = 0
    let wordBlur = 0

    switch (animation) {
      case 'stagger':
        wordY = interpolate(enter, [0, 1], [30, 0])
        break
      case 'elastic':
        wordY = interpolate(enter, [0, 1], [40, 0])
        wordScale = interpolate(enter, [0, 0.6, 1], [0.3, 1.15, 1])
        break
      case 'typewriter':
        wordOpacity = localFrame > 0 ? 1 : 0
        break
      case 'split-scale':
        wordScale = interpolate(enter, [0, 1], [0, 1])
        wordRotate = interpolate(enter, [0, 0.5, 1], [-8, 2, 0])
        break
      case 'blur-reveal':
        wordBlur = interpolate(enter, [0, 1], [12, 0])
        wordY = interpolate(enter, [0, 1], [8, 0])
        break
      case 'wave':
        wordY = Math.sin((frame * 0.12) + i * 0.8) * 12 * (1 - enter) + interpolate(enter, [0, 1], [20, 0])
        wordScale = interpolate(enter, [0, 0.5, 1], [0.7, 1.05, 1])
        break
      case 'bounce':
        wordY = interpolate(enter, [0, 0.4, 0.6, 0.8, 0.9, 1], [50, -8, 4, -2, 1, 0])
        wordScale = interpolate(enter, [0, 0.4, 0.6, 1], [0.5, 1.1, 0.95, 1])
        break
      case 'glitch':
        const glitchSeed = Math.sin(frame * 0.5 + i * 7.3) * 0.5 + 0.5
        const glitchActive = enter < 0.7 && glitchSeed > 0.6
        wordY = glitchActive ? (Math.random() - 0.5) * 16 : interpolate(enter, [0, 1], [12, 0])
        wordScale = glitchActive ? 1 + (Math.random() - 0.5) * 0.3 : 1
        wordOpacity = glitchActive ? (Math.random() > 0.3 ? 1 : 0) : interpolate(enter, [0, 0.4, 1], [0, 0.6, 1])
        break
      default:
        wordY = interpolate(enter, [0, 1], [18, 0])
        break
    }

    return (
      <span
        key={i}
        style={{
          display: 'inline-block',
          fontFamily,
          fontSize,
          fontWeight: style.fontWeight || (isPower ? 700 : 400),
          color,
          opacity: wordOpacity,
          transform: `translateY(${wordY}px) scale(${wordScale}) rotate(${wordRotate}deg)`,
          filter: wordBlur > 0 ? `blur(${wordBlur}px)` : undefined,
          lineHeight: 1.5,
          textShadow: (isPower || audioGlow > 0.1)
            ? `0 0 ${8 + (glowIntensity + audioGlow) * 20}px ${style.glowColor || color}60, 0 2px 8px rgba(0,0,0,0.5)`
            : '0 2px 8px rgba(0,0,0,0.5)',
        }}
      >
        {word}
      </span>
    )
  }

  return (
    <AbsoluteFill style={{
      justifyContent: 'center', alignItems: 'center',
      opacity,
    }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
        gap: '6px 12px', maxWidth: '85%',
        transform: `translateY(${translateY}px) scale(${scale * audioScale})`,
      }}>
        {words.map((word, i) => renderWord(word, i))}
      </div>
    </AbsoluteFill>
  )
}
